/**
 * LiveStreamingChatComposer — the bottom input row of the live chat overlay:
 * a text field ("Add comment...") plus a send button, and — for the AUDIENCE —
 * a gift/tip button beside the input.
 *
 * Ports Flutter `inputWidget` (`live_streaming_screen.dart:1298-1462`): a
 * `TextField` with the hint `'Add comment...'` (`:1346`), a send button
 * (`GestureDetector` → `addLiveMessage()`, `:1360-1362`), and the audience
 * gift/tip button (`:1382-1455`) — wired here (Task 7). Modeled on
 * `CommunityComposer` (`@/presentation/feature/community/components/CommunityComposer`)
 * but far simpler — a live session has no reply bar, no add-media, and no
 * go-live, so this reuses the shared `MessageInput` primitive with only a
 * placeholder + send, plus the audience gift button.
 *
 * Clear-on-proceed (Flutter parity, review_0 Must Fix 1): the draft is cleared
 * via the imperative `MessageInputHandle` **only** when the hook signals the
 * send proceeded past every guard. Flutter's `addLiveMessage` `return`s on the
 * internet guard (`:768-771`) and the empty-text guard (`:773-775`) BEFORE it
 * clears the field (`:802`), so an OFFLINE (or empty) send toasts and leaves the
 * typed draft intact. This composer therefore gates `.clear()` on
 * `onSendText(text) === true` (the hook's "proceeded" flag, Task 7's
 * `useLiveStreamingChat.sendText`) and adds NO pre-clear empty guard of its own
 * — the hook owns ALL guards (like Flutter's single `addLiveMessage` method) and
 * returns `false` for empty, so the clear is already correctly gated.
 *
 * Deviations from Flutter (UI latitude):
 *  - The send button stays ALWAYS mounted — the documented "all right-side icons
 *    stay mounted regardless of focus" web deviation the chat/community composers
 *    already take (`ChatComposer.tsx` / `CommunityComposer.tsx`). Flutter gates
 *    the send button on `_focusNode.hasFocus || _isBroadcaster`
 *    (`live_streaming_screen.dart:1356-1357`).
 *  - The send-icon typing-state rotation (`_isInputingText` `AnimatedRotation`,
 *    `:1364-1377`) is a cosmetic Flutter affordance; the `MessageInput` send
 *    button is the web equivalent (it carries its own typing-state transform),
 *    so no bespoke rotation is added here — a permitted styling deviation.
 *  - The input field is fully transparent (`theme.transparent`) so the hint /
 *    typed text read directly against the overlay's own translucent band rather
 *    than carrying a second dark scrim, matching Flutter's transparent input
 *    container over the video (`:1315-1321`). No hardcoded color.
 */

import React, { useCallback, useRef } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import {
  MessageInput,
  type MessageInputHandle,
} from '@/presentation/components/common/MessageInput';
import {
  ExpauseButton,
  ButtonVariant,
} from '@/presentation/components/common/controls/ExpauseButton';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface LiveStreamingChatComposerProps {
  /**
   * Send a live text message. Wired by the host to
   * `useLiveStreamingChat.sendText` (Task 7). Returns the hook's SYNCHRONOUS
   * "send proceeded past all guards" flag: `true` → the composer clears the
   * field; `false` (offline / empty / no current user) → the composer LEAVES the
   * typed draft intact, mirroring Flutter's guard-`return`-before-`clear`
   * ordering (`live_streaming_screen.dart:768-771` / `:773-775` → `:802`).
   */
  onSendText: (text: string) => boolean;
  /**
   * True iff the current user is the broadcaster (derived on the page from
   * `broadcasterFirebaseId === currentUser.firebaseId`). Flutter renders the
   * tip button only for the AUDIENCE (`!_isBroadcaster`,
   * `live_streaming_screen.dart:1385`), so the gift button is hidden for the
   * broadcaster.
   */
  isBroadcaster: boolean;
  /**
   * Audience gift/tip tap. The PAGE owns the sheet (hooks/components never open
   * sheets); this button just reports the tap. Opening the sheet naturally blurs
   * the composer, mirroring Flutter's `_focusNode.unfocus()` (`:1431`).
   */
  onGiftTap: () => void;
}

export const LiveStreamingChatComposer: React.FC<LiveStreamingChatComposerProps> = ({
  onSendText,
  isBroadcaster,
  onGiftTap,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  // Imperative handle to clear the input after a send that PROCEEDED. Flutter
  // clears `_textEditingController` at `:802`, strictly after the internet /
  // empty guards `return` (`:768-771` / `:773-775`).
  const messageInputRef = useRef<MessageInputHandle>(null);

  const handleSend = useCallback(
    (text: string): void => {
      // Clear ONLY when the send proceeded (offline / empty send returns `false`
      // and keeps the draft — review_0 Must Fix 1). No pre-clear empty guard
      // here: the hook owns every guard and returns `false` for empty text.
      const proceeded = onSendText(text);
      if (proceeded) messageInputRef.current?.clear();
    },
    [onSendText],
  );

  // The audience gift/tip button (Flutter `live_streaming_screen.dart:1382-1455`
  // — the non-broadcaster send-tip affordance) sits to the RIGHT of the input,
  // matching Flutter's action-row order (tip button rightmost, `:1382`). Wired
  // in Task 7. It reuses the common round-icon `ExpauseButton` (do not
  // hand-roll) tinted `theme.white` (Flutter `AppColors.white` for the audience,
  // `:1449`).
  //
  // Focus deviation (documented, same latitude the send button already takes,
  // above): Flutter gates the tip button on `!_focusNode.hasFocus` (`:1384`), but
  // the web keeps the right-side icons always mounted regardless of focus. The
  // remaining Flutter gates (`!_clearScreen && !_liveStreamingEnded &&
  // !_isOpeningChannel`, `:1386-1388`) are already handled by the overlay's mount
  // gate on the page, so the only condition re-derived here is the
  // audience-vs-broadcaster split (`!_isBroadcaster`, `:1385`).
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <MessageInput
          ref={messageInputRef}
          onSend={handleSend}
          disabled={false}
          loading={false}
          placeholder={l10n.community.liveStreamingPage.addComment}
          backgroundColor={theme.transparent}
          qa={{ id: 'live-chat-input' }}
          sendQa={{ id: 'live-chat-send' }}
        />
      </div>

      {!isBroadcaster && (
        <ExpauseButton
          {...qaAttr({ id: 'live-chat-gift' })}
          variant={ButtonVariant.round}
          icon="/assets/images/gift.png"
          iconColor={theme.white}
          onClick={onGiftTap}
          aria-label={l10n.gift.sendGift}
          style={{ marginRight: responsive.spacing.base, flexShrink: 0 }}
        />
      )}
    </div>
  );
};
